/**
 * When does a person's current load stop blocking them?
 *
 * Used by the "Cargabilidad por Horizonte Temporal" cards and the client-side
 * hiring-need fallback. It reads the fields GET /users/matrix returns for each
 * assignment, which the backend computes with the shared load rules
 * (app/api/domain/models/User/load_rules.py):
 *   - counts_toward_load: the row adds to total_capacity today (active state,
 *     dates overlapping, not shadowed by the proyecto, not the prácticas slot);
 *   - fecha_fin: the effective end, where null means open-ended (e.g. a T&M
 *     project with no end date);
 *   - oferta_probabilidad: 'alta' | 'media' | 'baja' | null.
 *
 * The page used to fall back to proyecto_fecha_fin/oferta_fecha_entrega when
 * fecha_fin was null. For an open-ended T&M project that picked the offer's long
 * past delivery date, so the person showed as "Disponible en 1 mes".
 */

export interface MatrixAssignment {
    team_type?: string | null
    fecha_fin?: string | null
    oferta_probabilidad?: string | null
    counts_toward_load?: boolean
}

export interface BlockingEnd {
    /** At least one counting project or alta/media offer keeps the person busy. */
    blocking: boolean
    /** Latest end of those assignments; null while blocking = open-ended (long term). */
    end: Date | null
}

const isHighImpact = (a: MatrixAssignment): boolean =>
    a.team_type === 'proyecto' || ['alta', 'media'].includes((a.oferta_probabilidad || '').toLowerCase())

export const blockingEnd = (assignments: MatrixAssignment[] | null | undefined): BlockingEnd => {
    let blocking = false
    let openEnded = false
    let latest: Date | null = null

    for (const a of assignments || []) {
        // Rows that don't add to today's load (closed, inactive, prácticas, shadowed) never block.
        if (a.counts_toward_load !== true) continue
        // Low-probability offers don't block the person.
        if (!isHighImpact(a)) continue
        blocking = true
        const end = a.fecha_fin ? new Date(a.fecha_fin) : null
        if (!end || Number.isNaN(end.getTime())) {
            openEnded = true
            continue
        }
        if (!latest || end > latest) latest = end
    }

    return { blocking, end: openEnded ? null : latest }
}
